import { OperationModels } from '../../../kernel/base-entities'
import { JobState } from './job'
import { StepState, StepTypeNames, TaskNames, TaskState } from './task'

export interface QueryParamsJobs {
  page: number
  page_size: number
  created_from?: string
  created_to?: string
  slot_from?: string
  slot_to?: string
  operative_models?: string
  general?: string
  stores?: string
  payment_methods?: string
  is_big_order?: boolean
  status?: string
  order?: string
  sources?: string
  zones?: string
  fleets?: string
  incidents?: string
  cities?: string
  countries?: string
  delay_status?: string
  rescheduled?: boolean
}

export const queryParamsJobsKeys: (keyof QueryParamsJobs)[] = [
  'page',
  'page_size',
  'created_from',
  'created_to',
  'slot_from',
  'slot_to',
  'operative_models',
  'general',
  'stores',
  'payment_methods',
  'is_big_order',
  'status',
  'order',
  'sources',
  'zones',
  'fleets',
  'incidents',
  'cities',
  'countries',
  'delay_status',
  'rescheduled'
]

export interface Order {
  field: FieldToOder
  sort: Sort
}

export enum FieldToOder {
  CREATED_AT = 'created_at',
  SLOT_FROM = 'slot_from'
}

export enum Sort {
  ASC = 'asc',
  DESC = 'desc'
}

interface Actions {
  rescheduled: Rescheduled
}

interface Rescheduled {
  last_slot_from: string
}

export interface HelaJob {
  id: string
  nebula_job: NebulaJobEvent
  custom_fields: CustomFields
  actions: Actions | null
}

export interface CustomFields {
  partial_status: string
}

interface NebulaJobEvent {
  id: string
  client_id: string
  job_number: string
  slot: Slot
  receiver: Receiver
  origin: Origin
  destination: Destination
  status: JobState
  issue_of: string | null
  delivery_code: string
  delivery_code_tries_limit: number
  can_change_payment_method: boolean
  original_prices: OriginalPrices
  job_comment: string | null
  delivery_options: DeliveryOption[]
  store: Store
  external_data: ExternalData | null
  packages: Package[]
  payment_info: PaymentInfo
  delivery_validations: DeliveryValidation[] | null
  pick_up_code: string | null
  is_big_order: boolean
  source: string
  tasks: Task[]
  items: Item[]
  created_at: string
  updated_at: string
}

export interface DeliveryValidation {
  type: string
  photo_url: string | null
  checked_at: string
}

export interface DeliveryOption {
  method: string
  comment: string
  cash_receiver: string
  phone_number: string
  photo_taken_at: string | null
  photo_url: null | string
}

interface Origin {
  name: string
  city: string
  address: string
  description: string
}

interface Destination {
  address: string
  address_two: string
  city: string
  description: string
}

export interface ExternalData {
  backoffice: StringMap
}

export interface StringMap {
  [data: string]: string
}

export interface Item {
  id: string
  status: ItemStatus
  attributes: Attributes
  comment: string
  found_quantity: number
  name: string
  origin: string
  photo_url: string
  presentation: Presentation
  presentation_v2: PresentationV2
  quantity: number
  is_substitute: boolean
  replaced_by: null
  reference: string
  package_name: string | null
  package_reference: string | null
  package_id: null | string
  claim_information: ClaimInformation | null
  rejected: Rejected
  rejection_information: RejectionInformation | null
  task_id: string
  updated_at: string
}

export interface Attributes {
  ean: string
  sku?: string
  plu: string
  location?: string
  category: string
}

export interface ClaimInformation {
  max_claim_date: string
  available_claim_actions: string[]
}

export interface Presentation {
  name: string
  unit: string
  price: Price
  weight: number
  quantity: number
  dimensions: number[]
}

export interface Price {
  amount: number
  currency_code: string
}

export interface PresentationV2 {
  unit: string
  price: number
  sub_unit: string
  quantity: number
  sub_quantity: number
}

export interface Rejected {
  rejected_by_driver: number
  rejected_by_user: number
}

export interface RejectionInformation {
  rejection_reason: string
  claim_action: string
  claim_state: string
  evidence: string[]
  comment: string
}

export interface OriginalPrices {
  taxes: number
  subtotal: number
  discounts: number
  shipping_fee: number
  payment_value: number
}

export interface Package {
  id: string
  store_id: string
  name: string
  reference: string
  zone: string
  state: string
}

export interface PaymentInfo {
  prices: Prices
  invoice: Invoice | null
  payment: Payment
  currency_code: string
}

export interface Invoice {
  reference: string
  attachments: string[]
}

export interface Payment {
  value: number
  method: string
  method_details: string | null
  metadata: string | null
  reference: string | null
  payment_status: string | null
  payment_status_details: string | null
  blocking_policy: string | null
}

export interface Prices {
  order_value: number
  shipping_fee: number
  taxes: number
  discounts: number
  subtotal: number
  attributes: Attribute[]
  additional_info: string[]
}

export interface Attribute {
  name: string
  type: string
  value: number
}

export interface Receiver {
  identification: string | null
  email: string
  phone_number: string
  first_name: string
  last_name: string
  wants_to_receive_notifications: boolean
}

export interface Slot {
  to: string
  from: string
  operational_model: OperationModels
}

export interface Store {
  id: string
  timezone: string
}

export interface StopMetada {
  order: number
  store_name: string
}

export interface Task {
  id: string
  type: TaskNames
  contact_info: ContactInfo | null
  departments: string[]
  fleet: Fleet | null
  status: TaskState
  job_id: string
  payment: string | null
  route: string | null
  steps: Step[]
  optimal_start_time: string
  optimal_end_time: string
  store_id?: string | null
  stop_metadata?: StopMetada | null
  updated_at: string
}

export interface ContactInfo {
  name: string
  phone: string
  photo_url: string
  resource_id: string
}

export interface Fleet {
  id: string
  name: string
}

export interface Step {
  id: string
  type: StepTypeNames
  status: StepState
  ordering: number
  resource_id: null | string
  optimal_start: string
  optimal_end: string
  actual_start: string | null
  actual_end: string | null
  active: boolean | null | undefined
  updated_at: string | null
}

export enum ItemStatus {
  PENDING = 'PENDING',
  ADDED = 'ADDED',
  REMOVED = 'REMOVED',
  CANCELLED = 'CANCELLED',
  REJECTED_BY_DRIVER = 'REJECTED_BY_DRIVER',
  REJECTED_BY_USER = 'REJECTED_BY_USER',
  REJECTED = 'REJECTED'
}
